import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import Icon from '../../components/AppIcon';
import api from '../../api/axios';

const MiniAudioPlayer = ({ url }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => { if (audio.duration) setProgress((audio.currentTime / audio.duration) * 100); };
    const onEnd = () => { setPlaying(false); setProgress(0); };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    return () => { audio.removeEventListener('timeupdate', onTime); audio.removeEventListener('ended', onEnd); };
  }, []);

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const ratio = Number(e.target.value) / 100;
    audio.currentTime = ratio * audio.duration;
    setProgress(Number(e.target.value));
  };

  return (
    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
      <audio ref={audioRef} src={url} preload="none" />
      <button onClick={toggle} className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 hover:bg-blue-700 transition-colors">
        <Icon name={playing ? 'Pause' : 'Play'} size={12} color="#fff" />
      </button>
      <input
        type="range"
        min={0}
        max={100}
        step={0.1}
        value={progress}
        onChange={seek}
        className="flex-1 h-1.5 accent-blue-500 cursor-pointer"
        aria-label="Seek recording"
      />
      <Icon name="Headphones" size={13} color="#6b7280" />
    </div>
  );
};

const LEAD_STATUS_STYLES = {
  queued: 'bg-blue-50 text-blue-700 border border-blue-200',
  dispatching: 'bg-blue-50 text-blue-700 border border-blue-200',
  calling: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
  connected: 'bg-green-50 text-green-700 border border-green-200',
  busy: 'bg-orange-50 text-orange-700 border border-orange-200',
  no_answer: 'bg-gray-100 text-gray-600 border border-gray-200',
  failed: 'bg-red-100 text-red-800 border border-red-200',
  voicemail: 'bg-purple-50 text-purple-700 border border-purple-200',
};

const LEAD_STATUS_DOT = {
  queued: 'bg-blue-500',
  dispatching: 'bg-blue-400',
  calling: 'bg-yellow-500',
  connected: 'bg-green-500',
  busy: 'bg-orange-500',
  no_answer: 'bg-gray-400',
  failed: 'bg-red-700',
  voicemail: 'bg-purple-500',
};

const STATUS_LABELS = {
  queued: 'In Queue',
  dispatching: 'Dispatching',
  calling: 'Calling',
  connected: 'Connected',
  busy: 'Busy',
  no_answer: 'No Answer',
  failed: 'Failed',
  voicemail: 'Voicemail',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const raw = String(dateStr);
  const d = new Date(raw.endsWith('Z') || raw.includes('+') ? raw : raw + 'Z');
  const opts = { timeZone: 'Asia/Kolkata' };
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', ...opts }) + ', ' +
    d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, ...opts });
};

const formatDuration = (seconds) => {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
};

const getFollowUpDisplay = (status) => {
  const map = {
    contacted: { label: 'Contacted', icon: 'CheckCircle2', className: 'bg-success/10 text-success border-success/20' },
    non_contacted: { label: 'Not Contacted', icon: 'Circle', className: 'bg-muted text-muted-foreground border-border hover:bg-warning/10 hover:text-warning' },
  };
  return map[status] || map.non_contacted;
};

// Fallback when the outcome-counts API has no bucket list (older backend)
const DEFAULT_OUTCOME_BUCKETS = [
  { key: 'interested', label: 'Interested' },
  { key: 'not_interested', label: 'Not Interested' },
  { key: 'callback', label: 'Callback' },
];

const POSITIVE_OUTCOMES = new Set(['interested', 'interested_dcp', 'interested_drp', 'interested_general', 'new_loan_seeker']);

const getOutcomeStyle = (key) => {
  if (POSITIVE_OUTCOMES.has(key)) {
    return { badge: 'bg-green-50 text-green-700', card: 'bg-green-50 border-green-200', text: 'text-green-700', dot: 'bg-green-500' };
  }
  if (key === 'callback') {
    return { badge: 'bg-orange-50 text-orange-700', card: 'bg-orange-50 border-orange-200', text: 'text-orange-700', dot: 'bg-orange-500' };
  }
  return { badge: 'bg-gray-100 text-gray-600', card: 'bg-gray-50 border-gray-200', text: 'text-gray-700', dot: 'bg-gray-400' };
};

const OutcomeBadge = ({ outcome, labels, className = 'px-2 py-0.5' }) => {
  if (!outcome) return <span className="text-gray-300 text-xs">—</span>;
  return (
    <span className={`inline-flex items-center rounded-full text-xs font-medium ${className} ${getOutcomeStyle(outcome).badge}`}>
      {labels?.[outcome] || outcome.replace(/_/g, ' ')}
    </span>
  );
};

// Lead attributes extracted from calls (Freed profile): stored as a JSON string on the lead
const parseExtractedData = (lead) => {
  if (!lead?.extractedData) return {};
  try {
    return JSON.parse(lead.extractedData) || {};
  } catch {
    return {};
  }
};

const formatRupees = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN')}` : String(value);
};

const formatYesNo = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const v = String(value).toLowerCase();
  if (v === 'yes' || v === 'true') return 'Yes';
  if (v === 'no' || v === 'false') return 'No';
  return String(value);
};

const LEAD_DETAIL_FIELDS = [
  { key: 'inquired_confirmed', label: 'Enquired', format: formatYesNo },
  { key: 'combined_loan_amount', label: 'Loan', format: formatRupees },
  { key: 'monthly_salary', label: 'Salary', format: formatRupees },
  { key: 'emis_on_time', label: 'EMIs on time', format: formatYesNo },
];

const LeadDetailsCell = ({ lead }) => {
  const data = parseExtractedData(lead);
  if (!LEAD_DETAIL_FIELDS.some((f) => data[f.key] !== null && data[f.key] !== undefined)) {
    return <span className="text-gray-300 text-xs">—</span>;
  }
  return (
    <span className="text-xs text-gray-600 whitespace-nowrap">
      {LEAD_DETAIL_FIELDS.map((f) => `${f.label}: ${f.format(data[f.key])}`).join(' · ')}
    </span>
  );
};

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leads');
  const [hoveredRow, setHoveredRow] = useState(null);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLeadPages, setTotalLeadPages] = useState(1);
  const [outcomeCounts, setOutcomeCounts] = useState({ profile: 'default', buckets: [] });
  const [conversationThread, setConversationThread] = useState([]);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [phoneSearch, setPhoneSearch] = useState('');
  const [phoneSearchDebounced, setPhoneSearchDebounced] = useState('');
  const [sortDir, setSortDir] = useState(null); // null | 'asc' | 'desc'

  // Debounce phone search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPhoneSearchDebounced(phoneSearch);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [phoneSearch]);

  const fetchOutcomeCounts = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/v1/campaigns/${id}/outcome-counts`);
      setOutcomeCounts(data?.data || { profile: 'default', buckets: [] });
    } catch (err) {
      console.error('Failed to fetch outcome counts:', err);
    }
  }, [id]);

  // Buckets come from the campaign's outcome profile (e.g. 8 buckets for Freed)
  const outcomeBuckets = useMemo(() => {
    if (outcomeCounts?.buckets?.length) return outcomeCounts.buckets;
    return DEFAULT_OUTCOME_BUCKETS.map((b) => ({ ...b, count: outcomeCounts?.[b.key] || 0 }));
  }, [outcomeCounts]);
  const outcomeLabels = useMemo(
    () => Object.fromEntries(outcomeBuckets.map((b) => [b.key, b.label])),
    [outcomeBuckets],
  );
  const showLeadDetails = outcomeCounts?.profile === 'freed';

  const fetchCampaign = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/v1/campaigns/${id}`);
      setCampaign(data?.data);
    } catch (err) {
      console.error('Failed to fetch campaign:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchLeads = useCallback(async () => {
    try {
      setLeadsLoading(true);
      const params = { page: currentPage - 1, size: 20 };
      if (statusFilter) params.status = statusFilter;
      if (selectedOutcome) params.outcome = selectedOutcome;
      if (phoneSearchDebounced) params.phone = phoneSearchDebounced;

      const { data } = await api.get(`/api/v1/campaigns/${id}/leads`, { params });
      setLeads(data?.data || []);
      setTotalLeadPages(data?.pagination?.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    } finally {
      setLeadsLoading(false);
    }
  }, [id, currentPage, statusFilter, selectedOutcome, phoneSearchDebounced]);

  useEffect(() => {
    fetchCampaign();
    fetchOutcomeCounts();
  }, [fetchCampaign, fetchOutcomeCounts]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Fetch transcript when a lead is selected
  useEffect(() => {
    if (!selectedLead?.sessionId) {
      setConversationThread([]);
      setRecordingUrl(null);
      return;
    }
    const fetchTranscript = async () => {
      setLoadingTranscript(true);
      setConversationThread([]);
      setRecordingUrl(null);
      try {
        const { data } = await api.get(`/api/v1/dashboard/call-details/${selectedLead.sessionId}`);
        const thread = data?.data?.conversationThread?.map(msg => ({
          sender: msg?.speaker?.toLowerCase(),
          text: msg?.message,
          time: msg?.time,
        })) || [];
        setConversationThread(thread);
        setRecordingUrl(data?.data?.recordingUrl || null);
      } catch (err) {
        console.error('Failed to fetch transcript:', err);
      } finally {
        setLoadingTranscript(false);
      }
    };
    fetchTranscript();
  }, [selectedLead?.sessionId]);

  const toggleContactStatus = async (lead) => {
    const oldStatus = lead?.contactStatus || 'non_contacted';
    const newStatus = oldStatus === 'contacted' ? 'non_contacted' : 'contacted';
    // Optimistic update
    setLeads((prev) => prev.map((l) => l?.id === lead?.id ? { ...l, contactStatus: newStatus } : l));
    if (selectedLead?.id === lead?.id) {
      setSelectedLead((prev) => ({ ...prev, contactStatus: newStatus }));
    }
    try {
      await api.put(`/api/v1/campaigns/${id}/leads/${lead?.id}/contact-status`, { contactStatus: newStatus });
    } catch (err) {
      console.error('Failed to update contact status:', err);
      // Revert on failure
      setLeads((prev) => prev.map((l) => l?.id === lead?.id ? { ...l, contactStatus: oldStatus } : l));
      if (selectedLead?.id === lead?.id) {
        setSelectedLead((prev) => ({ ...prev, contactStatus: oldStatus }));
      }
    }
  };

  const sortedLeads = useMemo(() => {
    if (!sortDir) return leads;
    return [...leads].sort((a, b) => {
      const da = a?.lastAttemptAt ? new Date(a.lastAttemptAt).getTime() : 0;
      const db = b?.lastAttemptAt ? new Date(b.lastAttemptAt).getTime() : 0;
      return sortDir === 'asc' ? da - db : db - da;
    });
  }, [leads, sortDir]);

  const toggleSort = () => {
    setSortDir((prev) => prev === null ? 'desc' : prev === 'desc' ? 'asc' : null);
  };

  const handleDownloadCsv = async () => {
    if (!selectedOutcome) return;
    setDownloading(true);
    try {
      const { data } = await api.get(`/api/v1/campaigns/${id}/leads`, {
        params: { outcome: selectedOutcome, page: 0, size: 10000 },
      });
      const rows = data?.data || [];
      const headers = ['Lead Name', 'Company', 'Phone', 'Status', 'Outcome', 'Duration (s)', 'AI Summary', 'Callback Time'];
      if (showLeadDetails) {
        headers.push('Enquired', 'Combined Loan Amount (₹)', 'Monthly Salary (₹)', 'EMIs On Time', 'Reschedules');
      }
      const csvRows = rows.map(r => {
        const row = [
          r.name || '', r.companyName || '', r.phone || '', r.status || '',
          outcomeLabels[r.outcome] || r.outcome || '',
          r.durationSeconds ?? '', r.aiSummary || '', r.requestedCallbackTime || '',
        ];
        if (showLeadDetails) {
          const d = parseExtractedData(r);
          row.push(
            d.inquired_confirmed != null ? formatYesNo(d.inquired_confirmed) : '',
            d.combined_loan_amount ?? '',
            d.monthly_salary ?? '',
            d.emis_on_time != null ? formatYesNo(d.emis_on_time) : '',
            r.rescheduleCount ?? 0,
          );
        }
        return row;
      });
      const csvContent = [headers, ...csvRows]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `campaign-${selectedOutcome}-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download CSV:', err);
    } finally {
      setDownloading(false);
    }
  };

  // Poll for active campaigns
  useEffect(() => {
    if (campaign?.status !== 'active') return;
    const interval = setInterval(() => {
      fetchCampaign();
      fetchLeads();
      fetchOutcomeCounts();
    }, 10000);
    return () => clearInterval(interval);
  }, [campaign?.status, fetchCampaign, fetchLeads, fetchOutcomeCounts]);

  if (loading) {
    return (
      <>
        <PrimaryNavigation />
        <div className="main-content min-h-screen bg-background flex items-center justify-center">
          <div className="flex items-center gap-3 text-gray-500">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
            <span className="text-sm">Loading campaign...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PrimaryNavigation />
      <div className="main-content min-h-screen bg-background flex flex-col">
        <NavigationBreadcrumb />
        <div className="px-4 md:px-6 py-6 md:py-8 flex-1">
            {/* Back + Title */}
            <div className="mb-6 flex items-center gap-3">
              <button
                onClick={() => navigate('/campaigns')}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
              >
                <Icon name="ArrowLeft" size={18} />
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-foreground">{campaign?.name || 'Campaign'}</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Campaign ID #{id} · {campaign?.totalLeads?.toLocaleString() || 0} Leads ·
                  <span className={`ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${LEAD_STATUS_STYLES?.[campaign?.status] || 'bg-gray-100'}`}>
                    {STATUS_LABELS?.[campaign?.status] || campaign?.status}
                  </span>
                </p>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              {[
                { label: 'Total Leads', value: campaign?.totalLeads || 0, icon: 'Users' },
                { label: 'Called', value: campaign?.leadsCalled || 0, icon: 'Phone' },
                { label: 'Connected', value: campaign?.leadsConnected || 0, icon: 'PhoneCall' },
                { label: 'Failed', value: campaign?.leadsFailed || 0, icon: 'PhoneOff' },
              ].map((stat) => (
                <div key={stat.label} className="bg-white rounded-xl border border-[#E5E7EB] px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name={stat.icon} size={14} className="text-gray-400" />
                    <span className="text-xs text-gray-500">{stat.label}</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{stat.value.toLocaleString()}</p>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 w-fit">
              {[{ key: 'leads', label: 'All Leads' }, { key: 'outcomes', label: 'Outcomes' }]?.map((tab) => (
                <button
                  key={tab?.key}
                  onClick={() => { setActiveTab(tab?.key); setSelectedOutcome(tab?.key === 'outcomes' ? (outcomeBuckets[0]?.key || null) : null); setCurrentPage(1); setPhoneSearch(''); setPhoneSearchDebounced(''); setSortDir(null); }}
                  className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeTab === tab?.key
                      ? 'bg-white text-[#2F55FF] shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab?.label}
                </button>
              ))}
            </div>

            {/* TAB 1: ALL LEADS */}
            {activeTab === 'leads' && (
              <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm overflow-hidden">
                {/* Filter row */}
                <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center gap-3">
                  <select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e?.target?.value); setCurrentPage(1); }}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white"
                  >
                    <option value="">All Status</option>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E5E7EB] bg-gray-50/60">
                        <th className="text-left px-5 py-3 font-medium text-gray-500 whitespace-nowrap">Lead Name</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Company</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Phone Number</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Status</th>
                        <th className="text-center px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Attempts</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Last Attempt</th>
                        <th className="text-right px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Duration</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Outcome</th>
                        {showLeadDetails && (
                          <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Lead Details</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {leadsLoading ? (
                        <tr>
                          <td colSpan={showLeadDetails ? 9 : 8} className="text-center py-12 text-gray-400">
                            <div className="flex items-center justify-center gap-3">
                              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                              <span>Loading leads...</span>
                            </div>
                          </td>
                        </tr>
                      ) : leads?.length === 0 ? (
                        <tr>
                          <td colSpan={showLeadDetails ? 9 : 8} className="text-center py-12 text-gray-400">
                            No leads found
                          </td>
                        </tr>
                      ) : (
                        leads?.map((lead) => (
                          <tr
                            key={lead?.id}
                            className="border-b border-[#F3F4F6] hover:bg-gray-50/60 transition-colors"
                            onMouseEnter={() => setHoveredRow(lead?.id)}
                            onMouseLeave={() => setHoveredRow(null)}
                          >
                            <td className="px-5 py-4 font-medium text-gray-900 whitespace-nowrap">{lead?.name || '—'}</td>
                            <td className="px-4 py-4 text-gray-600 whitespace-nowrap">{lead?.companyName || '—'}</td>
                            <td className="px-4 py-4 text-gray-600 whitespace-nowrap">{lead?.phone}</td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${LEAD_STATUS_STYLES?.[lead?.status] || 'bg-gray-100'}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${LEAD_STATUS_DOT?.[lead?.status] || 'bg-gray-400'}`} />
                                {STATUS_LABELS?.[lead?.status] || lead?.status}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-center text-gray-700 whitespace-nowrap">{lead?.attempts || 0}</td>
                            <td className="px-4 py-4 text-gray-600 whitespace-nowrap">{formatDate(lead?.lastAttemptAt)}</td>
                            <td className="px-4 py-4 text-right text-gray-700 whitespace-nowrap">{formatDuration(lead?.durationSeconds)}</td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <OutcomeBadge outcome={lead?.outcome} labels={outcomeLabels} />
                            </td>
                            {showLeadDetails && (
                              <td className="px-4 py-4"><LeadDetailsCell lead={lead} /></td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalLeadPages > 1 && (
                  <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#E5E7EB]">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 text-gray-500"
                    >
                      <Icon name="ChevronLeft" size={16} />
                    </button>
                    <span className="text-sm text-gray-500">Page {currentPage} of {totalLeadPages}</span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalLeadPages, p + 1))}
                      disabled={currentPage === totalLeadPages}
                      className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 text-gray-500"
                    >
                      <Icon name="ChevronRight" size={16} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* TAB 2: OUTCOMES */}
            {activeTab === 'outcomes' && (
              <div className="flex flex-col gap-4">
                {/* Outcome Cards */}
                <div className="flex flex-wrap gap-3 items-center">
                  {outcomeBuckets.map((card) => {
                    const style = getOutcomeStyle(card?.key);
                    return (
                      <button
                        key={card?.key}
                        onClick={() => { setSelectedOutcome(selectedOutcome === card?.key ? null : card?.key); setCurrentPage(1); setSelectedLead(null); setConversationThread([]); setSortDir(null); }}
                        className={`flex-1 ${outcomeBuckets.length > 3 ? 'min-w-[220px]' : ''} text-left px-4 py-2.5 rounded-xl border-2 transition-all flex items-center gap-3 ${style.card} ${
                          selectedOutcome === card?.key ? 'ring-2 ring-[#2F55FF] ring-offset-1' : 'hover:shadow-md'
                        }`}
                      >
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${style.dot}`} />
                        <span className={`text-sm font-semibold ${style.text}`}>{card?.label}</span>
                        <span className="ml-auto text-lg font-bold text-gray-900">{card?.count ?? 0}</span>
                      </button>
                    );
                  })}
                  {selectedOutcome && (
                    <button
                      onClick={handleDownloadCsv}
                      disabled={downloading}
                      className="ml-auto flex items-center gap-1.5 px-3 py-2 bg-[#2F55FF] text-white text-sm font-medium rounded-lg hover:bg-[#2545DD] disabled:opacity-50 transition-colors whitespace-nowrap"
                    >
                      <Icon name="Download" size={14} />
                      {downloading ? 'Downloading...' : 'Download CSV'}
                    </button>
                  )}
                </div>

                {/* Phone Search */}
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by phone number..."
                      value={phoneSearch}
                      onChange={(e) => setPhoneSearch(e?.target?.value)}
                      className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white w-64 focus:outline-none focus:ring-2 focus:ring-[#2F55FF]/20 focus:border-[#2F55FF]"
                    />
                  </div>
                </div>

                {/* Lead List + Detail — fixed height (≥ 10 rows visible) regardless of how many
                    rows the bucket cards wrap into; the page scrolls, the table scrolls inside */}
                <div className="flex gap-4 h-[max(620px,calc(100vh-240px))]">
                  <div className="flex-1 bg-white rounded-2xl border border-[#E5E7EB] shadow-sm overflow-hidden flex flex-col">
                    <div className="overflow-y-auto flex-1">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-gray-50/90 backdrop-blur-sm">
                          <tr className="border-b border-[#E5E7EB]">
                            <th className="text-left px-5 py-2.5 font-medium text-gray-500">Lead Name</th>
                            <th className="text-left px-4 py-2.5 font-medium text-gray-500">Company</th>
                            <th className="text-left px-4 py-2.5 font-medium text-gray-500">Phone</th>
                            <th className="text-left px-4 py-2.5 font-medium text-gray-500">Outcome</th>
                            <th className="text-right px-4 py-2.5 font-medium text-gray-500">Duration</th>
                            <th
                              className="text-left px-4 py-2.5 font-medium text-gray-500 cursor-pointer select-none hover:text-gray-700"
                              onClick={toggleSort}
                            >
                              <span className="inline-flex items-center gap-1">
                                Called At
                                {sortDir === 'asc' ? <Icon name="ChevronUp" size={13} /> : sortDir === 'desc' ? <Icon name="ChevronDown" size={13} /> : <Icon name="ChevronsUpDown" size={13} className="opacity-40" />}
                              </span>
                            </th>
                            <th className="text-left px-4 py-2.5 font-medium text-gray-500">AI Summary</th>
                            {showLeadDetails && (
                              <th className="text-left px-4 py-2.5 font-medium text-gray-500 whitespace-nowrap">Lead Details</th>
                            )}
                            <th className="text-left px-4 py-2.5 font-medium text-gray-500">Callback Time</th>
                            <th className="text-center px-4 py-2.5 font-medium text-gray-500">Follow-up</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedLeads?.map((lead) => (
                            <tr
                              key={lead?.id}
                              onClick={() => setSelectedLead(lead)}
                              className={`border-b border-[#F3F4F6] hover:bg-gray-50 cursor-pointer transition-colors ${selectedLead?.id === lead?.id ? 'bg-blue-50/40' : ''}`}
                            >
                              <td className="px-5 py-3 font-medium text-gray-900">{lead?.name || '—'}</td>
                              <td className="px-4 py-3 text-gray-600">{lead?.companyName || '—'}</td>
                              <td className="px-4 py-3 text-gray-600">{lead?.phone}</td>
                              <td className="px-4 py-3">
                                <OutcomeBadge outcome={lead?.outcome} labels={outcomeLabels} />
                              </td>
                              <td className="px-4 py-3 text-right text-gray-600">{formatDuration(lead?.durationSeconds)}</td>
                              <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDate(lead?.lastAttemptAt)}</td>
                              <td className="px-4 py-3 text-gray-500 max-w-[160px] truncate text-xs">{lead?.aiSummary || '—'}</td>
                              {showLeadDetails && (
                                <td className="px-4 py-3"><LeadDetailsCell lead={lead} /></td>
                              )}
                              <td className="px-4 py-3 text-gray-500 text-xs">{lead?.requestedCallbackTime || '—'}</td>
                              <td className="px-4 py-3 text-center">
                                {(() => {
                                  const display = getFollowUpDisplay(lead?.contactStatus);
                                  return (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleContactStatus(lead); }}
                                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-base ${display.className}`}
                                    >
                                      <Icon name={display.icon} size={12} />
                                      {display.label}
                                    </button>
                                  );
                                })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    {totalLeadPages > 1 && (
                      <div className="flex items-center justify-end gap-2 px-5 py-2 border-t border-[#E5E7EB]">
                        <button
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 text-gray-500"
                        >
                          <Icon name="ChevronLeft" size={16} />
                        </button>
                        <span className="text-sm text-gray-500">Page {currentPage} of {totalLeadPages}</span>
                        <button
                          onClick={() => setCurrentPage((p) => Math.min(totalLeadPages, p + 1))}
                          disabled={currentPage === totalLeadPages}
                          className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 text-gray-500"
                        >
                          <Icon name="ChevronRight" size={16} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Right Panel: Lead Detail */}
                  <div className="w-80 flex-shrink-0 bg-white rounded-2xl border border-[#E5E7EB] shadow-sm flex flex-col overflow-hidden">
                    {selectedLead ? (
                      <>
                        <div className="px-4 py-3 border-b border-[#E5E7EB] bg-gray-50/60">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-[#2F55FF]/10 flex items-center justify-center">
                                <span className="text-xs font-bold text-[#2F55FF]">{selectedLead?.name?.charAt(0) || '?'}</span>
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-900">{selectedLead?.name || 'Unknown'}</p>
                                <p className="text-xs text-gray-500">
                                  {selectedLead?.phone}
                                  {selectedLead?.companyName ? ` · ${selectedLead.companyName}` : ''}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => setSelectedLead(null)}
                              className="p-1 rounded-lg hover:bg-gray-100 text-gray-400"
                            >
                              <Icon name="X" size={14} />
                            </button>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1"><Icon name="Clock" size={11} />{formatDuration(selectedLead?.durationSeconds)}</span>
                            <span className="flex items-center gap-1"><Icon name="Phone" size={11} />{selectedLead?.attempts || 0} attempts</span>
                          </div>
                        </div>

                        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                          {/* Status & Outcome */}
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${LEAD_STATUS_STYLES?.[selectedLead?.status] || 'bg-gray-100'}`}>
                              {STATUS_LABELS?.[selectedLead?.status] || selectedLead?.status}
                            </span>
                            {selectedLead?.outcome && (
                              <OutcomeBadge outcome={selectedLead?.outcome} labels={outcomeLabels} className="px-2.5 py-1" />
                            )}
                            {showLeadDetails && (selectedLead?.rescheduleCount || 0) > 0 && (
                              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                <Icon name="RotateCcw" size={11} />
                                {selectedLead.rescheduleCount} reschedule{selectedLead.rescheduleCount === 1 ? '' : 's'}
                              </span>
                            )}
                          </div>

                          {/* Lead Details (Freed profile) */}
                          {showLeadDetails && (
                            <div className="bg-gray-50 rounded-lg px-3 py-2">
                              <p className="text-xs font-semibold text-gray-700 mb-1">Lead Details</p>
                              <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                                {(() => {
                                  const data = parseExtractedData(selectedLead);
                                  return [
                                    ['Enquired', formatYesNo(data.inquired_confirmed)],
                                    ['EMIs on time', formatYesNo(data.emis_on_time)],
                                    ['Combined loan', formatRupees(data.combined_loan_amount)],
                                    ['Monthly salary', formatRupees(data.monthly_salary)],
                                  ].map(([label, value]) => (
                                    <div key={label}>
                                      <p className="text-[10px] text-gray-400">{label}</p>
                                      <p className="text-xs font-medium text-gray-800">{value}</p>
                                    </div>
                                  ));
                                })()}
                              </div>
                            </div>
                          )}

                          {/* AI Summary */}
                          {selectedLead?.aiSummary && (
                            <div className="bg-blue-50 rounded-lg px-3 py-2">
                              <p className="text-xs font-semibold text-blue-700 mb-0.5">AI Summary</p>
                              <p className="text-xs text-blue-600 leading-relaxed">{selectedLead?.aiSummary}</p>
                            </div>
                          )}

                          {/* Requested Callback Time */}
                          {selectedLead?.requestedCallbackTime && (
                            <div className="bg-orange-50 rounded-lg px-3 py-2">
                              <p className="text-xs font-semibold text-orange-700 mb-0.5">Requested Callback Time</p>
                              <p className="text-xs text-orange-600 leading-relaxed">{selectedLead?.requestedCallbackTime}</p>
                            </div>
                          )}

                          {/* Transcript */}
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                              <Icon name="MessageSquare" size={12} />
                              Conversation
                            </p>
                            {!loadingTranscript && recordingUrl && recordingUrl !== '#' && (
                              <div className="mb-3">
                                <MiniAudioPlayer url={recordingUrl} />
                              </div>
                            )}
                            {loadingTranscript ? (
                              <div className="flex items-center justify-center py-8">
                                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                                <span className="ml-2 text-xs text-gray-400">Loading transcript...</span>
                              </div>
                            ) : conversationThread?.length > 0 ? (
                              <div className="space-y-2.5">
                                {conversationThread?.map((msg, idx) => (
                                  <div key={idx} className={`flex ${msg?.sender === 'customer' ? 'justify-start' : 'justify-end'}`}>
                                    <div className={`max-w-[85%] rounded-lg px-3 py-2 ${
                                      msg?.sender === 'customer'
                                        ? 'bg-gray-100 text-gray-800'
                                        : 'bg-[#2F55FF] text-white'
                                    }`}>
                                      <p className={`text-[10px] font-semibold mb-0.5 ${msg?.sender === 'customer' ? 'text-gray-500' : 'text-white/70'}`}>
                                        {msg?.sender === 'customer' ? 'Customer' : 'Agent'}
                                      </p>
                                      <p className="text-xs leading-relaxed">{msg?.text}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400 text-center py-4">No transcript available</p>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                          <Icon name="MessageSquare" size={20} className="text-gray-400" />
                        </div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Select a Lead</p>
                        <p className="text-xs text-gray-400">Click any lead to view details</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
      </div>
    </>
  );
};

export default CampaignDetail;
